import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { hapticFeedback } from '../../utils/telegram';
import ExchangeConnectionModal from '../common/ExchangeConnectionModal/ExchangeConnectionModal';
import FormattedNumber from '../common/FormattedNumber/FormattedNumber';
import Loader from '../common/Loader/Loader';
import './ExchangeManagement.css';

const ExchangeManagement = () => {
  const { t } = useLanguage();
  const { getExchangeList, disconnectExchange, connectExchange } = useApi();

  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [error, setError] = useState('');

  // Load exchanges on mount
  useEffect(() => {
    loadExchanges();
  }, []);

  const loadExchanges = async () => {
    try {
      setLoading(true);
      const response = await getExchangeList();
      // API возвращает объект с полем data, где data - это массив бирж
      setExchanges(response.data || []);
    } catch (error) {
      console.error('Error loading exchanges:', error);
      setError(t('loadExchangesError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConnectExchange = async (connectionData) => {
    try {
      await connectExchange(connectionData);
      hapticFeedback.success();
      await loadExchanges(); // Refresh the list
    } catch (error) {
      console.error('Error connecting exchange:', error);
      throw error;
    }
  };

  const handleDisconnectExchange = async (exchangeId) => {
    try {
      setDisconnecting(exchangeId);
      hapticFeedback.medium();

      await disconnectExchange(exchangeId);
      hapticFeedback.success();

      // Refresh the list
      await loadExchanges();
    } catch (error) {
      console.error('Error disconnecting exchange:', error);
      hapticFeedback.error();
      setError(t('disconnectExchangeError'));
    } finally {
      setDisconnecting(null);
    }
  };

  const handleAddExchange = () => {
    hapticFeedback.light();
    setShowConnectionModal(true);
  };

  const getExchangeDisplayName = (exchangeId) => {
    const exchangeNames = {
      'bitget': 'Bitget',
      'bingx': 'BingX',
      'bybit': 'Bybit'
    };
    return exchangeNames[exchangeId] || exchangeId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return '#34c759';
      case 'error': return '#ff3b30';
      case 'connecting': return '#ff9500';
      default: return '#8e8e93';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected': return t('connected');
      case 'error': return t('connectionError');
      case 'connecting': return t('connecting');
      default: return t('disconnected');
    }
  };

  if (loading) {
    return <Loader fullPage={true} />;
  }

  return (
    <div className="exchange-management">
      <div className="page-header">
        <h1 className="page-title">{t('exchangeManagement')}</h1>
        <p className="page-description">{t('exchangeManagementDescription')}</p>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* Connected Exchanges */}
      <div className="exchanges-section">
        <div className="section-header">
          <h2 className="section-title">{t('connectedExchanges')}</h2>
          <button
            className="add-exchange-button"
            onClick={handleAddExchange}
          >
            + {t('addExchange')}
          </button>
        </div>

        {exchanges.length === 0 ? (
          <div className="empty-states">
            <div className="empty-icon">🏦</div>
            <h3>{t('noExchangesConnected')}</h3>
            <p>{t('noExchangesDescription')}</p>
            <button
              className="primary-button"
              onClick={handleAddExchange}
            >
              {t('connectFirstExchange')}
            </button>
          </div>
        ) : (
          <div className="exchanges-grid">
            {exchanges.map((exchange) => (
              <div key={exchange.id} className="exchange-card">
                <div className="exchange-header">
                  <div className="exchange-info">
                    <h3 className="exchange-name">
                      {getExchangeDisplayName(exchange.exchangeId)}
                    </h3>
                    <div
                      className="exchange-status"
                      style={{ color: getStatusColor(exchange.status) }}
                    >
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: getStatusColor(exchange.status) }}
                      />
                      {getStatusText(exchange.status)}
                    </div>
                  </div>
                  <button
                    className="disconnect-button"
                    onClick={() => handleDisconnectExchange(exchange.id)}
                    disabled={disconnecting === exchange.id}
                  >
                    {disconnecting === exchange.id ? '...' : '×'}
                  </button>
                </div>

                <div className="exchange-details">
                  <div className="detail-row">
                    <span className="detail-label">{t('apiKey')}</span>
                    <span className="detail-value masked">
                      {exchange.apiKey}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">{t('lastUpdate')}</span>
                    <span className="detail-value">
                      {exchange.lastConnectionTest
                        ? new Date(exchange.lastConnectionTest).toLocaleString()
                        : '-'
                      }
                    </span>
                  </div>
                </div>

                {exchange.balances && exchange.balances.length > 0 && (
                  <div className="exchange-balances">
                    <h4 className="balances-title">{t('balance')}</h4>
                  </div>
                )}

                {exchange.status === 'connected' && exchange.totalBalance && (
                  <div className="total-balance">
                    <span className="total-label">{t('totalBalance')}</span>
                    <span className="total-value">
                      <FormattedNumber value={exchange.totalBalance} suffix="USDT" />
                    </span>
                  </div>
                )}

                {exchange.status === 'error' && exchange.connectionErrorMessage && (
                  <div className="error-message-box">
                    {exchange.connectionErrorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Section */}
      {exchanges.length > 0 && (
        <div className="summary-section">
          <h2 className="section-title">{t('summary')}</h2>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">{t('totalExchanges')}</div>
              <div className="summary-value">
                {exchanges.filter(ex => ex.status === 'connected').length}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('totalBalanceAllExchanges')}</div>
              <div className="summary-value">
                <FormattedNumber
                  value={exchanges.reduce((sum, ex) => sum + (ex.totalBalance || 0), 0)}
                  suffix="USDT"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connection Modal */}
      <ExchangeConnectionModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        onConnect={handleConnectExchange}
      />
    </div>
  );
};

export default ExchangeManagement; 